// Card-model Google placement chrome — Discover, Display network,
// Gmail, and YouTube in-feed. The Google counterpart to cards.tsx.
//
// Same contract as the Meta cards: these WRAP the media rather than
// overlay it, so the creative stays fully visible and is judged against
// the surrounding furniture. Geometry is in `u` = 1% of media width.
//
// One difference from Meta, and it's the important one: Google DOES
// stamp markers onto the creative on these placements — the AdChoices
// roundel always, an "Ad" badge on native/Discover units. Those are
// drawn by <GoogleAdMarkers> INSIDE the media box (googleOverlays.tsx),
// because they cover whatever the creative put in that corner.
//
// Performance Max auto-assembles these units from the uploaded assets:
// the headline, description, business name and button are Google's
// text, laid out by Google, over our image. So the copy here is
// placeholder scaffolding by design — what the operator is judging is
// how much of the unit the furniture takes and where it lands, not the
// words.

import type { CSSProperties, ReactNode } from 'react';
import { Icon, Avatar } from './icons';
import { displayDomain } from './overlays';
import { GoogleAdMarkers } from './googleOverlays';
import type { ChromeDensity, ChromeIdentity } from './types';

const FONT = "Roboto, 'Google Sans', -apple-system, 'Segoe UI', Arial, sans-serif";
const HEADLINE = 'Lorem ipsum dolor sit amet';
const DESC = 'Consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore.';

type CardProps = {
  identity:        ChromeIdentity;
  u:               number;
  density:         ChromeDensity;
  showSponsored?:  boolean;
  showEngagement?: boolean;
  children:        ReactNode;
};

const cardBase: CSSProperties = {
  background: '#fff', color: '#202124', overflow: 'hidden',
  fontFamily: FONT, textAlign: 'left', width: '100%'
};

// The media slot plus the markers Google stamps on it. Every card here
// routes its children through this so no placement can forget them.
function MarkedMedia({
  children, u, density, showAdBadge, showSponsored
}: {
  children:        ReactNode;
  u:               number;
  density:         ChromeDensity;
  showAdBadge?:    boolean;
  showSponsored?:  boolean;
}) {
  return (
    <div style={{ position: 'relative' }}>
      {children}
      <GoogleAdMarkers u={u} density={density} showAdBadge={showAdBadge} showSponsored={showSponsored} />
    </div>
  );
}

// ── Google Discover ──────────────────────────────────────────────────
// The feed card in the Google app / Chrome new-tab. Where PMax's square
// and portrait marketing images land. Furniture sits below the image:
// headline first, then the advertiser row.

export function DiscoverCard({ identity, u, density, showSponsored = true, children }: CardProps) {
  const compact = density === 'compact';
  const cta = identity.ctaText || 'Shop now';
  const p = compact ? 0.6 : 1;
  const domain = displayDomain(identity.linkDomain);

  return (
    <div style={{ ...cardBase, borderRadius: u * 2, border: '1px solid #dadce0' }}>
      <MarkedMedia u={u} density={density} showAdBadge showSponsored={showSponsored}>{children}</MarkedMedia>

      <div style={{ padding: `${u * 3 * p}px ${u * 3.2}px ${u * 2 * p}px` }}>
        <div style={{ fontSize: u * 4, lineHeight: 1.3, fontWeight: 400, color: '#202124' }}>
          {HEADLINE}
        </div>
        {!compact && (
          <div style={{ marginTop: u * 1.4, fontSize: u * 3.1, lineHeight: 1.4, color: '#5f6368' }}>
            {DESC}
          </div>
        )}
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: u * 2,
        padding: `0 ${u * 3.2}px ${u * 3 * p}px`
      }}>
        <Avatar logoUrl={identity.logoUrl} name={identity.name} size={u * 5.6} />
        <div style={{ minWidth: 0, fontSize: u * 3, color: '#5f6368', lineHeight: 1.3 }}>
          <b style={{ color: '#202124', fontWeight: 500 }}>{identity.name}</b>
          {!compact && <span style={{ display: 'block' }}>{domain}</span>}
        </div>
        {/* Discover's CTA is a quiet outlined pill, not a filled button. */}
        <span style={{
          marginLeft: 'auto', flex: 'none',
          border: '1px solid #dadce0', borderRadius: u * 4,
          padding: `${u * 1.6 * p}px ${u * 3}px`,
          fontSize: u * 3, fontWeight: 500, color: '#1a73e8', whiteSpace: 'nowrap'
        }}>
          {cta}
        </span>
      </div>
    </div>
  );
}

// ── Google Display network ───────────────────────────────────────────
// The responsive display unit: Google assembles image + headline +
// description + business name + button and drops it into a publisher's
// page, at whatever size that slot happens to be. The heaviest
// furniture of any Google placement — worth previewing precisely
// because it's the one most likely to crowd a creative.

export function DisplayCard({ identity, u, density, showSponsored = true, children }: CardProps) {
  const compact = density === 'compact';
  const cta = identity.ctaText || 'Shop now';
  const p = compact ? 0.6 : 1;

  return (
    <div style={{ ...cardBase, border: '1px solid #dadce0' }}>
      <MarkedMedia u={u} density={density} showSponsored={showSponsored}>{children}</MarkedMedia>

      <div style={{ padding: `${u * 2.6 * p}px ${u * 3.2}px` }}>
        <div style={{ fontSize: u * 3.8, lineHeight: 1.25, fontWeight: 500, color: '#202124' }}>
          {HEADLINE}
        </div>
        {!compact && (
          <div style={{ marginTop: u * 1.2, fontSize: u * 3, lineHeight: 1.4, color: '#5f6368' }}>
            {DESC}
          </div>
        )}
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: u * 2.4,
        padding: `0 ${u * 3.2}px ${u * 3 * p}px`
      }}>
        <span style={{
          fontSize: u * 2.9, color: '#5f6368', minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
        }}>
          {identity.name}
        </span>
        {/* Filled blue button — the Display default when the advertiser
            hasn't supplied a brand colour. */}
        <span style={{
          marginLeft: 'auto', flex: 'none',
          background: '#1a73e8', color: '#fff', borderRadius: u * 0.6,
          padding: `${u * 1.8 * p}px ${u * 3.4}px`,
          fontSize: u * 3.1, fontWeight: 500, whiteSpace: 'nowrap'
        }}>
          {cta}
        </span>
      </div>
    </div>
  );
}

// ── Gmail (Promotions tab) ───────────────────────────────────────────
// The expanded Gmail ad: a mail-shaped unit with the sender row and the
// yellow "Ad" chip above the creative. Included because PMax genuinely
// serves it and because the framing is unlike anything else — the
// creative reads as an email body, and the top of it sits under a
// header row rather than at the top of the viewport.

export function GmailCard({ identity, u, density, showSponsored = true, children }: CardProps) {
  const compact = density === 'compact';
  const cta = identity.ctaText || 'Shop now';
  const p = compact ? 0.6 : 1;
  const domain = displayDomain(identity.linkDomain);

  return (
    <div style={{ ...cardBase, border: '1px solid #dadce0', borderRadius: u * 1 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: u * 2.2,
        padding: `${u * 2.6 * p}px ${u * 3.2}px`, borderBottom: '1px solid #f1f3f4'
      }}>
        {showSponsored && (
          <span style={{
            flex: 'none', background: '#fdd663', color: '#3c4043', borderRadius: u * 0.5,
            padding: `${u * 0.6}px ${u * 1.4}px`, fontSize: u * 2.4, fontWeight: 700
          }}>
            Ad
          </span>
        )}
        <div style={{ minWidth: 0, lineHeight: 1.3 }}>
          <b style={{
            display: 'block', fontSize: u * 3.4, fontWeight: 500, color: '#202124',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
          }}>
            {HEADLINE}
          </b>
          {!compact && (
            <span style={{ fontSize: u * 2.9, color: '#5f6368' }}>{identity.name} &middot; {domain}</span>
          )}
        </div>
        <span style={{ marginLeft: 'auto', flex: 'none', color: '#5f6368' }}>
          <Icon name="dots" size={u * 4.2} />
        </span>
      </div>

      <MarkedMedia u={u} density={density} showSponsored={showSponsored}>{children}</MarkedMedia>

      <div style={{ padding: `${u * 2.6 * p}px ${u * 3.2}px ${u * 3 * p}px` }}>
        {!compact && (
          <div style={{ fontSize: u * 3.1, lineHeight: 1.4, color: '#5f6368', marginBottom: u * 2 }}>
            {DESC}
          </div>
        )}
        <span style={{
          display: 'inline-block',
          background: '#1a73e8', color: '#fff', borderRadius: u * 0.6,
          padding: `${u * 1.8 * p}px ${u * 3.4}px`,
          fontSize: u * 3.1, fontWeight: 500, whiteSpace: 'nowrap'
        }}>
          {cta}
        </span>
      </div>
    </div>
  );
}

// ── YouTube in-feed ──────────────────────────────────────────────────
// The video ad that appears as a row in search results, watch-next, and
// the home feed — as opposed to in-stream, which plays before content.
// The creative is shown as a thumbnail with a duration chip stamped on
// it, so the bottom-right corner is spoken for.

export function YouTubeInFeedCard({ identity, u, density, showSponsored = true, children }: CardProps) {
  const compact = density === 'compact';
  const p = compact ? 0.6 : 1;

  return (
    <div style={{ ...cardBase, background: '#0f0f0f', color: '#fff' }}>
      <div style={{ position: 'relative' }}>
        {children}
        <GoogleAdMarkers u={u} density={density} showSponsored={showSponsored} />
        {/* Duration chip — YouTube stamps this on every thumbnail. */}
        <span style={{
          position: 'absolute', right: u * 1.6, bottom: u * 1.6,
          background: 'rgba(0,0,0,.8)', color: '#fff', borderRadius: u * 0.5,
          padding: `${u * 0.5}px ${u * 1.1}px`, fontSize: u * 2.2, fontWeight: 500
        }}>
          0:15
        </span>
      </div>

      <div style={{ display: 'flex', gap: u * 2.4, padding: `${u * 3 * p}px ${u * 3.2}px ${u * 3.4 * p}px` }}>
        <Avatar logoUrl={identity.logoUrl} name={identity.name} size={u * 7} />
        <div style={{ minWidth: 0, lineHeight: 1.3 }}>
          <b style={{ display: 'block', fontSize: u * 3.5, fontWeight: 500, color: '#fff' }}>
            {HEADLINE}
          </b>
          <span style={{ display: 'block', marginTop: u * 0.8, fontSize: u * 2.9, color: '#aaa' }}>
            {identity.name}
          </span>
          {showSponsored && (
            <span style={{
              display: 'inline-block', marginTop: u * 1.2,
              background: '#ffd400', color: '#0f0f0f', borderRadius: u * 0.4,
              padding: `${u * 0.5}px ${u * 1.2}px`, fontSize: u * 2.2, fontWeight: 700
            }}>
              Ad
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
